import type { Locale } from "../i18n/languages";

/**
 * Loads the one extra font stylesheet this page's writing system needs, and nothing else.
 *
 * WHY THIS EXISTS AT ALL — the short version; the long one is at the top of
 * src/styles/fonts.css. Space Grotesk covers latin only, so nine of the twenty-four locales
 * had no face of ours. The textbook answer is one family plus unicode-range, and it fails
 * here for a specific reason: the language switcher prints all 24 endonyms in their own
 * scripts on EVERY page, so every page meets every script and every page would download
 * every face. An English reader would have paid megabytes to render 日本語 in a menu.
 *
 * So the choice is made from the page's language instead, which the pre-render already knows:
 * it writes <html lang> per locale, and there are 72 static pages, one per locale x route.
 *
 * WHAT THIS RENDERS. A plain <link rel="stylesheet">. React 19 hoists a stylesheet element to
 * <head> and de-duplicates it by href, so this can sit inside the tree rather than needing a
 * change to scripts/prerender.mjs — which is what keeps the pre-render free of any knowledge
 * about fonts. The sheet is in the pre-rendered HTML, so it applies with JavaScript disabled.
 *
 * `precedence` is what makes React treat the element as a hoistable resource rather than as
 * ordinary markup. It deliberately does NOT decide anything: a script sheet only ever defines
 * --font-script / --font-mono-script, which tokens.css reads through var() at use time, so
 * these sheets and the main bundle can load in either order.
 */

/**
 * Locale -> stylesheet under public/fonts. A locale that is absent needs no second face:
 * Latin-script languages are already covered by Space Grotesk, Vietnamese included.
 *
 * Grouped rather than one-per-locale, because the writing system is what a font serves, not
 * the language: ru/uk/kk share one Cyrillic file, and zh-Hant and yue share one Traditional
 * Chinese file. Adding a language means adding a row here only if it brings a NEW script.
 */
const SCRIPT_OF: Partial<Record<Locale, string>> = {
  ru: "cyrillic",
  uk: "cyrillic",
  kk: "cyrillic",
  ar: "arabic",
  hi: "devanagari",
  bn: "bengali",
  th: "thai",
  ja: "jp",
  ko: "kr",
  "zh-Hans": "sc",
  "zh-Hant": "tc",
  yue: "tc",
};

export interface ScriptFontProps {
  locale: Locale;
}

export function ScriptFont({ locale }: ScriptFontProps) {
  const script = SCRIPT_OF[locale];
  if (!script) return null;

  return <link rel="stylesheet" href={`/fonts/script-${script}.css`} precedence="default" />;
}
